import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
} from "@reown/appkit/react";

import useContract from "../../hooks/useContract";

/* =========================================================
   CONSTANTS
========================================================= */

const USD_DECIMALS = 18;
const BSC_CHAIN_ID = 56;

/* =========================================================
   HELPERS
========================================================= */

function toSafeNumber(value) {
  try {
    const number = Number(value ?? 0);

    return Number.isFinite(number)
      ? number
      : 0;
  } catch {
    return 0;
  }
}

function formatNumber(
  value,
  maximumFractionDigits = 2
) {
  const number = toSafeNumber(value);

  return new Intl.NumberFormat("en-GB", {
    maximumFractionDigits,
  }).format(number);
}

function formatInteger(value) {
  return formatNumber(value, 0);
}

function formatRaw(
  value,
  decimals = 18,
  maximumFractionDigits = 2
) {
  try {
    if (value === undefined || value === null) {
      return "0";
    }

    let raw;

    if (typeof value === "bigint") {
      raw = value.toString();
    } else {
      raw = String(value);
    }

    if (!/^-?\d+$/.test(raw)) {
      return "0";
    }

    const negative = raw.startsWith("-");

    const unsigned = negative
      ? raw.slice(1)
      : raw;

    if (decimals === 0) {
      return negative
        ? `-${new Intl.NumberFormat("en-GB").format(
            Number(unsigned)
          )}`
        : new Intl.NumberFormat("en-GB").format(
            Number(unsigned)
          );
    }

    const padded = unsigned.padStart(
      decimals + 1,
      "0"
    );

    const integerPart =
      padded.slice(0, -decimals) || "0";

    const decimalPart =
      padded.slice(-decimals);

    const trimmedDecimal =
      decimalPart
        .slice(0, maximumFractionDigits)
        .replace(/0+$/, "");

    const formattedInteger =
      new Intl.NumberFormat("en-GB").format(
        Number(integerPart)
      );

    const result =
      trimmedDecimal.length > 0
        ? `${formattedInteger}.${trimmedDecimal}`
        : formattedInteger;

    return negative
      ? `-${result}`
      : result;
  } catch {
    return "0";
  }
}

function formatUsd(value) {
  return formatRaw(
    value,
    USD_DECIMALS,
    2
  );
}

function formatDate(timestamp) {
  const value = toSafeNumber(timestamp);

  if (value <= 0) {
    return "—";
  }

  return new Date(
    value * 1000
  ).toLocaleString("en-GB");
}

function shortAddress(address) {
  if (!address) {
    return "";
  }

  return `${address.slice(
    0,
    6
  )}...${address.slice(-4)}`;
}

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good morning";
  }

  if (hour < 18) {
    return "Good afternoon";
  }

  return "Good evening";
}

function getTierLabel(tierId) {
  const tier = toSafeNumber(tierId);

  if (tier === 1) {
    return "Tier 1";
  }

  if (tier === 2) {
    return "Tier 2";
  }

  if (tier === 3) {
    return "Tier 3";
  }

  return "Unassigned";
}

function getTierStatus(tier) {
  if (!tier) {
    return {
      text: "Inactive",
      className:
        "border-white/10 bg-white/[0.03] text-[#969696]",
    };
  }

  if (tier.claimable) {
    return {
      text: "Claimable",
      className:
        "border-blue-400/20 bg-blue-400/10 text-blue-300",
    };
  }

  if (tier.pending) {
    return {
      text: "Pending",
      className:
        "border-[#ffcc66]/20 bg-[#ffcc66]/10 text-[#ffcc66]",
    };
  }

  return {
    text: "Active",
    className:
      "border-emerald-400/20 bg-emerald-400/10 text-emerald-300",
  };
}

function getErrorMessage(error) {
  if (!error) {
    return "Unable to load dashboard data.";
  }

  if (error?.code === 4001) {
    return "Wallet connection was rejected.";
  }

  if (
    error?.code === "ACTION_REJECTED"
  ) {
    return "Wallet request was rejected.";
  }

  if (
    error?.message?.toLowerCase?.().includes(
      "user rejected"
    )
  ) {
    return "Wallet request was rejected.";
  }

  return (
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    "Unable to load dashboard data."
  );
}

/* =========================================================
   COUNTDOWN
========================================================= */

function useCountdown(endAt) {
  const [countdown, setCountdown] =
    useState("—");

  useEffect(() => {
    const endingTimestamp =
      toSafeNumber(endAt);

    if (endingTimestamp <= 0) {
      setCountdown("—");
      return undefined;
    }

    const updateCountdown = () => {
      const difference =
        endingTimestamp * 1000 -
        Date.now();

      if (difference <= 0) {
        setCountdown("Ended");
        return;
      }

      const totalSeconds = Math.floor(
        difference / 1000
      );

      const days = Math.floor(
        totalSeconds / 86400
      );

      const hours = Math.floor(
        (totalSeconds % 86400) /
          3600
      );

      const minutes = Math.floor(
        (totalSeconds % 3600) /
          60
      );

      const seconds =
        totalSeconds % 60;

      setCountdown(
        `${days}d ${hours}h ${minutes}m ${seconds}s`
      );
    };

    updateCountdown();

    const interval =
      window.setInterval(
        updateCountdown,
        1000
      );

    return () => {
      window.clearInterval(
        interval
      );
    };
  }, [endAt]);

  return countdown;
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  label,
  value,
  note,
  accent = false,
}) {
  return (
    <div
      className={`rounded-[14px] border p-5 transition duration-200 hover:-translate-y-[2px] ${
        accent
          ? "border-[#ffcc66]/20 bg-[#ffcc66]/[0.04]"
          : "border-white/[0.08] bg-[#111314]"
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#747474]">
        {label}
      </p>

      <p
        className={`mt-3 break-words text-[22px] font-bold ${
          accent
            ? "text-[#ffcc66]"
            : "text-white"
        }`}
      >
        {value}
      </p>

      {note ? (
        <p className="mt-2 text-[11px] text-[#666]">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/* =========================================================
   SECTION TITLE
========================================================= */

function SectionTitle({
  eyebrow,
  title,
  right,
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        {eyebrow ? (
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#ffcc66]/70">
            {eyebrow}
          </p>
        ) : null}

        <h2 className="mt-1 text-[18px] font-bold text-white">
          {title}
        </h2>
      </div>

      {right || null}
    </div>
  );
}

/* =========================================================
   PROGRESS BAR
========================================================= */

function ProgressBar({
  value,
  maximum,
  formatted = false,
}) {
  const current =
    toSafeNumber(value);

  const max =
    toSafeNumber(maximum);

  const percentage =
    max > 0
      ? Math.min(
          (current / max) * 100,
          100
        )
      : 0;

  const currentLabel = formatted
    ? formatUsd(value)
    : formatInteger(value);

  const maximumLabel = formatted
    ? formatUsd(maximum)
    : formatInteger(maximum);

  return (
    <div>
      <div className="mb-2 flex justify-between text-[11px]">
        <span className="text-[#8c8c8c]">
          {currentLabel} /{" "}
          {maximumLabel}
        </span>

        <span className="text-[#ffcc66]">
          {percentage.toFixed(1)}%
        </span>
      </div>

      <div className="h-[7px] overflow-hidden rounded-full bg-black/50">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#ffcc66] to-[#b89041] transition-all duration-500"
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
}

/* =========================================================
   DASHBOARD
========================================================= */

export default function Dashboard() {
  /* =======================================================
     APPKIT
  ======================================================= */

  const { open } = useAppKit();

  const {
    address,
    isConnected,
  } = useAppKitAccount();

  const {
    chainId: appKitChainId,
  } = useAppKitNetwork();

  /* =======================================================
     CONTRACT
  ======================================================= */

  const {
    getUserDashboardData,
    disconnectWallet,
  } = useContract();

  /* =======================================================
     LOCAL STATE
  ======================================================= */

  const [dashboardData, setDashboardData] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  /* =======================================================
     BSC CHECK
  ======================================================= */

  const currentChainId =
    Number(appKitChainId || 0);

  const isBsc =
    currentChainId ===
    BSC_CHAIN_ID;

  /* =======================================================
     LOAD DASHBOARD
  ======================================================= */

  const loadDashboard =
    useCallback(async () => {
      if (!isConnected || !address) {
        setDashboardData(null);
        return;
      }

      if (!isBsc) {
        setDashboardData(null);

        setError(
          "Please switch your wallet to BNB Smart Chain."
        );

        return;
      }

      try {
        setLoading(true);
        setError("");

        const result =
          await getUserDashboardData(
            address
          );

        setDashboardData(
          result || null
        );
      } catch (loadError) {
        console.error(
          "Dashboard load failed:",
          loadError
        );

        setError(
          getErrorMessage(
            loadError
          )
        );
      } finally {
        setLoading(false);
      }
    }, [
      address,
      getUserDashboardData,
      isBsc,
      isConnected,
    ]);

  /* =======================================================
     LOAD WHEN APPKIT WALLET CHANGES
  ======================================================= */

  useEffect(() => {
    if (
      isConnected &&
      address &&
      isBsc
    ) {
      loadDashboard();
    } else {
      setDashboardData(null);

      if (
        isConnected &&
        address &&
        !isBsc
      ) {
        setError(
          "Please switch your wallet to BNB Smart Chain."
        );
      } else {
        setError("");
      }
    }
  }, [
    address,
    isBsc,
    isConnected,
    loadDashboard,
  ]);

  /* =======================================================
     DISCONNECT
  ======================================================= */

  const handleDisconnect =
    useCallback(async () => {
      try {
        setDashboardData(null);
        setError("");

        /*
         * AppKit owns the real wallet
         * connection.
         */
        await disconnectWallet?.();
      } catch (disconnectError) {
        console.error(
          "Disconnect failed:",
          disconnectError
        );
      }
    }, [disconnectWallet]);

  /* =======================================================
     CONNECT
  ======================================================= */

  const handleConnect =
    useCallback(async () => {
      try {
        setError("");

        /*
         * Opens the AppKit modal.
         *
         * AppKit handles:
         * MetaMask
         * Bitget Wallet
         * WalletConnect wallets
         * Trust Wallet
         * etc.
         */
        await open();
      } catch (connectError) {
        console.error(
          "Wallet connection failed:",
          connectError
        );

        setError(
          getErrorMessage(
            connectError
          )
        );
      }
    }, [open]);

  /* =======================================================
     DATA
  ======================================================= */

  const leagueId =
    dashboardData?.currentLeagueId ??
    dashboardData?.leagueId ??
    0;

  const league =
    dashboardData?.league ??
    dashboardData?.currentLeague ??
    null;

  const walletData =
    dashboardData?.wallet ??
    dashboardData?.walletData ??
    null;

  const kpData =
    dashboardData?.kpBreakdown ??
    dashboardData?.kpData ??
    null;

  const tierData =
    dashboardData?.tier ??
    dashboardData?.tierData ??
    null;

  const tierId =
    dashboardData?.tierId ??
    dashboardData?.estimatedReward
      ?.tierId ??
    walletData?.tier ??
    0;

  const estimatedReward =
    dashboardData?.estimatedReward
      ?.estimatedKind ??
    dashboardData?.estimatedRewardRaw ??
    0;

  const claimStatus =
    dashboardData?.claimStatus ??
    null;

  const extendedStats =
    dashboardData?.extendedStats ??
    dashboardData?.globalStats ??
    null;

  const globalTradingGoal =
    dashboardData
      ?.globalTradingGoalUsd ??
    dashboardData
      ?.globalTradingGoal ??
    0;

  const rewardTokenData =
    dashboardData?.rewardToken ?? {
      address: "",
      symbol: "KIND",
      decimals: 18,
    };

  const rewardTokenSymbol =
    rewardTokenData?.symbol ||
    "KIND";

  const rewardTokenDecimals =
    toSafeNumber(
      rewardTokenData?.decimals ??
        18
    );

  /* =======================================================
     COUNTDOWN
  ======================================================= */

  const countdown =
    useCountdown(
      league?.endAt
    );

  /* =======================================================
     TIER STATUS
  ======================================================= */

  const tierStatus =
    useMemo(
      () =>
        getTierStatus(
          tierData
        ),
      [tierData]
    );

  /* =======================================================
     REMAINING WALLETS
  ======================================================= */

  const remainingWallets =
    useMemo(() => {
      if (!tierData) {
        return 0;
      }

      return Math.max(
        toSafeNumber(
          tierData.minWallets
        ) -
          toSafeNumber(
            tierData.qualifiedWallets
          ),
        0
      );
    }, [tierData]);

  /* =======================================================
     RANK
  ======================================================= */

  const isRanked =
    toSafeNumber(
      walletData?.currentRank
    ) > 0;

  /* =======================================================
     TIER LABEL
  ======================================================= */

  const activeTierLabel =
    getTierLabel(tierId);

  /* =======================================================
     NOT CONNECTED
  ======================================================= */

  if (!isConnected) {
    return (
      <main className="min-h-screen bg-[#050b08] px-4 py-14 text-white">
        <div className="mx-auto max-w-[1100px]">
          <section className="rounded-[18px] border border-[#ffcc66]/15 bg-gradient-to-r from-[#111513] via-[#12150f] to-[#28240f] p-8 md:p-12">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ffcc66]">
              Kind Trading League
            </p>

            <h1 className="mt-3 text-3xl font-black md:text-5xl">
              Connect your trading wallet
            </h1>

            <p className="mt-4 max-w-xl text-sm leading-7 text-[#888]">
              Connect the wallet used
              to buy and sell KIND to
              view your KP, rank,
              reward tier and estimated
              league rewards.
            </p>

            {error ? (
              <div className="mt-5 rounded-lg border border-red-400/20 bg-red-400/10 p-3 text-xs text-red-300">
                {error}
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleConnect}
              className="mt-7 rounded-[9px] bg-[#ffcc66] px-6 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-[#191a1b] transition hover:bg-[#ffe09a]"
            >
              Connect Wallet
            </button>

            <p className="mt-4 text-[10px] text-[#555]">
              Supports MetaMask, Bitget
              Wallet, Trust Wallet and
              other wallets available
              through AppKit.
            </p>
          </section>
        </div>
      </main>
    );
  }

  /* =======================================================
     WRONG NETWORK
  ======================================================= */

  if (!isBsc) {
    return (
      <main className="min-h-screen bg-[#050b08] px-4 py-14 text-white">
        <div className="mx-auto max-w-[1100px]">
          <section className="rounded-[18px] border border-red-400/20 bg-gradient-to-r from-[#111513] via-[#15110f] to-[#21100d] p-8 md:p-12">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-300">
              Wrong Network
            </p>

            <h1 className="mt-3 text-3xl font-black md:text-5xl">
              Switch to BNB Smart Chain
            </h1>

            <p className="mt-4 max-w-xl text-sm leading-7 text-[#888]">
              Your wallet is connected, but
              the selected network is not
              BNB Smart Chain Mainnet.
            </p>

            <div className="mt-5 rounded-lg border border-red-400/20 bg-red-400/10 p-4 text-xs text-red-300">
              Current chain:{" "}
              {currentChainId || "Unknown"}
              <br />
              Required chain: BSC Mainnet
              (56)
            </div>

            <button
              type="button"
              onClick={() => open()}
              className="mt-6 rounded-[9px] bg-[#ffcc66] px-6 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-[#191a1b] transition hover:bg-[#ffe09a]"
            >
              Open Wallet
            </button>
          </section>
        </div>
      </main>
    );
  }

  /* =======================================================
     CONNECTED DASHBOARD
  ======================================================= */

  return (
    <main className="min-h-screen bg-[#050b08] px-4 py-8 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1100px]">

        {/* =================================================
            ERROR
        ================================================= */}

        {error ? (
          <div className="mb-5 rounded-[12px] border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-300">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <span>{error}</span>

              <button
                type="button"
                onClick={loadDashboard}
                className="rounded-lg border border-red-300/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em]"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : null}

        {/* =================================================
            WELCOME
        ================================================= */}

        <section className="relative overflow-hidden rounded-[18px] border border-[#ffcc66]/15 bg-gradient-to-r from-[#111513] via-[#12150f] to-[#302a0d] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.3)] md:p-8">

          {/* Desktop Disconnect */}

          <button
            type="button"
            onClick={handleDisconnect}
            className="
              absolute right-8 top-7
              hidden
              rounded-[9px]
              border border-red-400/20
              bg-transparent
              px-4 py-2
              text-[9px]
              font-bold
              uppercase
              tracking-[0.12em]
              text-red-300
              transition
              hover:bg-red-400/10
              md:block
            "
          >
            Disconnect
          </button>

          <div className="flex flex-col">

            {/* Main desktop row */}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_260px] md:items-stretch">

              {/* Left Welcome */}

              <div className="flex items-center gap-5">

                <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full border border-[#ffcc66]/20 bg-black/40 shadow-[0_0_30px_rgba(255,204,102,0.08)]">
                  <img
                    src="/kindlogo.png"
                    alt="Kind Trading League"
                    className="h-12 w-12 object-contain"
                  />
                </div>

                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#ffcc66]">
                    {getGreeting()}
                  </p>

                  <h1 className="mt-1 text-2xl font-black md:text-3xl">
                    Welcome to Kind Trading League
                  </h1>

                  <p className="mt-1 font-mono text-[11px] text-[#777]">
                    {shortAddress(address)}
                    {" · "}
                    {activeTierLabel}
                  </p>

                  <span
                    className={`mt-3 inline-flex rounded-full border px-3 py-1 text-[8px] font-bold uppercase tracking-[0.13em] ${
                      walletData?.qualified
                        ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
                        : "border-[#ffcc66]/20 bg-[#ffcc66]/10 text-[#ffcc66]"
                    }`}
                  >
                    {walletData?.qualified
                      ? "On-chain qualified"
                      : "Not qualified"}
                  </span>
                </div>
              </div>

              {/* Active League */}

              <div className="flex w-full flex-col justify-center rounded-[12px] border border-white/[0.06] bg-black/20 px-5 py-4 md:mt-8">
                <p className="text-[9px] uppercase tracking-[0.16em] text-[#777]">
                  Active league
                </p>

                <div className="mt-1 flex items-center justify-between gap-4">
                  <p className="text-lg font-bold text-[#ffcc66]">
                    League #
                    {String(
                      leagueId || 0
                    )}
                  </p>

                  <p className="text-xs text-[#777]">
                    {countdown}
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Disconnect */}

            <button
              type="button"
              onClick={handleDisconnect}
              className="
                mt-5
                w-full
                rounded-[9px]
                border border-red-400/20
                bg-transparent
                px-4 py-3
                text-[9px]
                font-bold
                uppercase
                tracking-[0.12em]
                text-red-300
                transition
                hover:bg-red-400/10
                md:hidden
              "
            >
              Disconnect
            </button>

          </div>
        </section>

        {/* =================================================
            PERSONAL OVERVIEW
        ================================================= */}

        <section className="mt-5 rounded-[18px] border border-[#ffcc66]/15 bg-gradient-to-br from-[#111311] via-[#111411] to-[#24210d] p-6 md:p-7">

          <SectionTitle
            eyebrow="Your league status"
            title="Personal Trading Overview"
            right={
              <span
                className={`rounded-full border px-3 py-1 text-[8px] font-bold uppercase tracking-[0.13em] ${tierStatus.className}`}
              >
                {tierStatus.text}
              </span>
            }
          />

          <div className="grid gap-5 md:grid-cols-3">

            <div>
              <p className="text-[9px] uppercase tracking-[0.15em] text-[#666]">
                Username
              </p>

              <p className="mt-2 text-lg font-bold">
                {walletData?.username ||
                  "Anon"}
              </p>

              <p className="mt-1 break-all font-mono text-[10px] text-[#666]">
                {address}
              </p>
            </div>

            <div>
              <p className="text-[9px] uppercase tracking-[0.15em] text-[#666]">
                Current tier
              </p>

              <p className="mt-2 text-lg font-bold text-[#ffcc66]">
                {activeTierLabel}
              </p>

              <p className="mt-1 text-[10px] text-[#666]">
                {isRanked
                  ? `Rank #${String(
                      walletData.currentRank
                    )}`
                  : "Currently unranked"}
              </p>
            </div>

            <div>
              <p className="text-[9px] uppercase tracking-[0.15em] text-[#666]">
                Estimated reward
              </p>

              <p className="mt-2 text-lg font-bold text-[#ffcc66]">
                {formatRaw(
                  estimatedReward,
                  rewardTokenDecimals,
                  4
                )}{" "}
                {rewardTokenSymbol}
              </p>

              <p className="mt-1 text-[10px] text-[#666]">
                Based on current tier KP
                share
              </p>
            </div>
          </div>

          {tierData ? (
            <div className="mt-7 border-t border-white/[0.06] pt-5">

              <ProgressBar
                value={
                  tierData.qualifiedWallets
                }
                maximum={
                  tierData.minWallets
                }
              />

              {remainingWallets > 0 ? (
                <p className="mt-3 text-[11px] text-[#ffcc66]">
                  Only{" "}
                  {formatInteger(
                    remainingWallets
                  )}{" "}
                  more qualified traders
                  are needed to unlock
                  this reward pool.
                </p>
              ) : (
                <p className="mt-3 text-[11px] text-emerald-300">
                  Minimum qualified-wallet
                  target reached.
                </p>
              )}
            </div>
          ) : null}
        </section>

        {/* =================================================
            PERSONAL SNAPSHOT
        ================================================= */}

        <section className="mt-7">

          <SectionTitle
            eyebrow="Personal data"
            title="Your Trading Snapshot"
            right={
              <button
                type="button"
                onClick={loadDashboard}
                disabled={loading}
                className="rounded-[8px] border border-[#ffcc66]/20 px-4 py-2 text-[9px] font-bold uppercase tracking-[0.13em] text-[#ffcc66] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading
                  ? "Refreshing"
                  : "Refresh"}
              </button>
            }
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

            <StatCard
              label="Current Rank"
              value={
                isRanked
                  ? `#${String(
                      walletData.currentRank
                    )}`
                  : "Unranked"
              }
              note="Current league position"
              accent
            />

            <StatCard
              label="Current League KP"
              value={`${formatInteger(
                kpData?.currentLeagueKp
              )} KP`}
              note="KP earned this league"
            />

            <StatCard
              label="Pending KP"
              value={`${formatInteger(
                kpData?.totalPendingKp
              )} KP`}
              note="KP carried from pending tiers"
            />

            <StatCard
              label="Total KP"
              value={`${formatInteger(
                kpData?.totalKp
              )} KP`}
              note="Current plus pending KP"
              accent
            />

            <StatCard
              label="League Volume"
              value={`$${formatUsd(
                walletData
                  ?.currentLeagueVolumeUsd
              )}`}
              note="Current league volume"
            />

            <StatCard
              label="Lifetime Volume"
              value={`$${formatUsd(
                walletData
                  ?.lifetimeVolumeUsd
              )}`}
              note="All recorded league volume"
            />

            <StatCard
              label="Buy Volume"
              value={`$${formatUsd(
                walletData
                  ?.buyVolumeUsd
              )}`}
              note="Eligible BUY volume"
            />

            <StatCard
              label="Sell Volume"
              value={`$${formatUsd(
                walletData
                  ?.sellVolumeUsd
              )}`}
              note="Eligible SELL volume"
            />

          </div>
        </section>

        {/* =================================================
            REWARDS
        ================================================= */}

        <section className="mt-7">

          <SectionTitle
            eyebrow="Reward details"
            title="Personal Rewards"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

            <StatCard
              label="Estimated Reward"
              value={`${formatRaw(
                estimatedReward,
                rewardTokenDecimals,
                4
              )} ${rewardTokenSymbol}`}
              accent
            />

            <StatCard
              label="Available to Claim"
              value={`${formatRaw(
                claimStatus
                  ?.claimableAmount,
                rewardTokenDecimals,
                4
              )} ${rewardTokenSymbol}`}
              note={
                claimStatus?.expired
                  ? "Claim period expired"
                  : claimStatus?.claimed
                  ? "Already claimed"
                  : "Current claimable balance"
              }
            />

            <StatCard
              label="Rewards Earned"
              value={`${formatRaw(
                walletData
                  ?.totalRewardsEarned,
                rewardTokenDecimals,
                4
              )} ${rewardTokenSymbol}`}
            />

            <StatCard
              label="Rewards Claimed"
              value={`${formatRaw(
                walletData
                  ?.totalRewardsClaimed,
                rewardTokenDecimals,
                4
              )} ${rewardTokenSymbol}`}
            />

          </div>
        </section>

        {/* =================================================
            GLOBAL STATISTICS
        ================================================= */}

        <section className="mt-7">

          <SectionTitle
            eyebrow="League analytics"
            title="Global Statistics"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

            <StatCard
              label="League Trading Volume"
              value={`$${formatUsd(
                league
                  ?.totalVolumeUsd
              )}`}
              note="Current league volume"
              accent
            />

            <StatCard
              label="Qualified Wallets"
              value={formatInteger(
                league
                  ?.totalQualifiedWallets
              )}
              note="Wallets joined through first BUY"
            />

            <StatCard
              label="Total KP Generated"
              value={`${formatInteger(
                league?.totalKp
              )} KP`}
              note="Current league total"
            />

            <StatCard
              label="Reward Pool"
              value={`${formatRaw(
                league?.rewardPool,
                rewardTokenDecimals,
                2
              )} ${rewardTokenSymbol}`}
              note="Current league allocation"
              accent
            />

            <StatCard
              label="Total Buy Volume"
              value={`$${formatUsd(
                league
                  ?.totalBuyVolumeUsd
              )}`}
            />

            <StatCard
              label="Total Sell Volume"
              value={`$${formatUsd(
                league
                  ?.totalSellVolumeUsd
              )}`}
            />

            <StatCard
              label="Pending Tier Pools"
              value={`${formatRaw(
                extendedStats
                  ?.pendingRewardPools,
                rewardTokenDecimals,
                2
              )} ${rewardTokenSymbol}`}
            />

            <StatCard
              label="Pending KP"
              value={`${formatInteger(
                extendedStats
                  ?.pendingKpTotal
              )} KP`}
            />

            <StatCard
              label="Pending Tiers"
              value={formatInteger(
                extendedStats
                  ?.pendingTiersCount
              )}
            />

            <StatCard
              label="Visible Leaderboard"
              value={formatInteger(
                league
                  ?.visibleLeaderboardSize
              )}
              note="Publicly displayed wallets"
            />

            <StatCard
              label="KIND Burned"
              value={`${formatRaw(
                extendedStats
                  ?.kindBurned,
                rewardTokenDecimals,
                2
              )} ${rewardTokenSymbol}`}
            />

            <StatCard
              label="League Ends"
              value={countdown}
              note={formatDate(
                league?.endAt
              )}
            />

          </div>
        </section>

        {/* =================================================
            GLOBAL GOAL
        ================================================= */}

        <section className="mt-7 rounded-[16px] border border-[#ffcc66]/15 bg-[#111314] p-6">

          <SectionTitle
            eyebrow="Community target"
            title="Global Trading Volume Goal"
          />

          <ProgressBar
            value={
              league
                ?.totalVolumeUsd
            }
            maximum={
              globalTradingGoal
            }
            formatted
          />

          <div className="mt-4 flex flex-col justify-between gap-2 text-[11px] sm:flex-row">

            <span className="text-[#777]">
              Current: $
              {formatUsd(
                league
                  ?.totalVolumeUsd
              )}
            </span>

            <span className="text-[#ffcc66]">
              Goal: $
              {formatUsd(
                globalTradingGoal
              )}
            </span>

          </div>
        </section>

      </div>
    </main>
  );
}